"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Download, Loader2, Share, X } from "lucide-react";
import { toast } from "sonner";

import type {
  CSSProperties,
  KeyboardEvent,
  PointerEvent,
  ReactElement,
} from "react";

import { AtriamMark } from "@/components/branding/atriam-logo";
import { trackEvent } from "@/lib/analytics/events";

import "./install-prompt-banner.css";

/**
 * "Install Atriam" prompt — a glass card in the top-right toast stack.
 *
 * Why this exists: Chrome deprecated its auto-show install mini-infobar in
 * Chrome 76. Since then a PWA is only prompted to install when the site
 * itself listens for `beforeinstallprompt`, stashes the event, and calls
 * `.prompt()` on a user gesture — otherwise the user only finds Install
 * buried in Chrome's overflow menu. iOS Safari never fires
 * `beforeinstallprompt` at all, so iOS users need a static "Tap Share →
 * Add to Home Screen" hint or they get nothing.
 *
 * A/B test (`INSTALL_PROMPT_EXPERIMENT_ID`): each browser is assigned, once
 * and for good, one of two cards —
 *  - `ember`: the whole card is one ember-glass install button,
 *  - `slide`: drag the app icon along a glass track to install.
 * PostHog funnel `pwa_install_prompt_shown` → `pwa_install_prompt_engaged`
 * (browser dialog opened) → `pwa_install_prompt_resolved` with
 * `outcome = accepted`, broken down by `variant`.
 *
 * Behaviour:
 *  - hides when the app is already running standalone (`display-mode:
 *    standalone` or iOS Safari's `navigator.standalone`),
 *  - hides for a random 1–10 day cooldown after the user dismisses (the X
 *    or a swipe),
 *  - on Chromium/Edge: waits for `beforeinstallprompt`, then surfaces the
 *    assigned card, which calls the captured event's `.prompt()`,
 *  - on iOS Safari: surfaces a static hint card immediately (no install API
 *    exists there), outside the A/B split,
 *  - dismisses immediately once `appinstalled` fires.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const INSTALL_PROMPT_EXPERIMENT_ID = "install-prompt-2026-09";

export type InstallPromptVariant = "ember" | "slide";
type ShownVariant = InstallPromptVariant | "ios_hint";

const VARIANT_STORAGE_KEY = "atriam.pwa.install-prompt.variant";
// We persist the *expiry* timestamp (not the dismissal time) so the random
// cooldown is rolled once, at dismissal, and stays stable across the navigation
// re-checks below instead of re-rolling on every `beforeinstallprompt` fire.
const SNOOZE_STORAGE_KEY = "atriam.pwa.install-banner.snooze-until";
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_SNOOZE_DAYS = 10;
const TOAST_ID = "atriam.pwa.install";

// Mounted in the root layout, so without a route gate the prompt fires on the
// logged-out login page (covering the marketing headline), on /offline (where
// installing can't work), and over the marketing pages' header CTA — all
// flagged in the 2026-06-09 UI audit. Install is an engaged-user ask: keep it
// to the authenticated app shell.
const SUPPRESSED_PREFIXES = [
  "/auth",
  "/offline",
  "/pricing",
  "/compare",
  "/policy",
  "/service",
  "/status",
  "/support",
  "/claim",
] as const;

function isSuppressedRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  if (pathname === "/") return true;
  return SUPPRESSED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari sets `navigator.standalone` when launched from the home screen.
  if ((window.navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }
  return false;
}

function isIosBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // iPad on modern iOS reports as Mac with touch capability — feature-detect that.
  const isIpadDesktopUA =
    ua.includes("Macintosh") &&
    typeof document !== "undefined" &&
    "ontouchend" in document;
  return /iPad|iPhone|iPod/.test(ua) || isIpadDesktopUA;
}

function readSnoozeUntil(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function snooze(): void {
  if (typeof window === "undefined") return;
  // Random 1–10 day cooldown so the re-prompt feels organic, never a fixed nag.
  const days = Math.floor(Math.random() * MAX_SNOOZE_DAYS) + 1;
  try {
    window.localStorage.setItem(
      SNOOZE_STORAGE_KEY,
      String(Date.now() + days * DAY_MS),
    );
  } catch {
    // private mode / storage quota — ignore, user will just be re-prompted.
  }
}

function isSnoozed(): boolean {
  const until = readSnoozeUntil();
  return until !== null && Date.now() < until;
}

/** Sticky once assigned; `roll` in [0, 1) splits new browsers 50/50. */
export function resolveInstallPromptVariant(
  persisted: string | null,
  roll: number,
): InstallPromptVariant {
  if (persisted === "ember" || persisted === "slide") return persisted;
  return roll < 0.5 ? "ember" : "slide";
}

function assignVariant(): InstallPromptVariant {
  let persisted: string | null = null;
  try {
    persisted = window.localStorage.getItem(VARIANT_STORAGE_KEY);
  } catch {
    // ponytail: storage blocked means a fresh roll per page load; Chrome
    // never offers install in incognito, so this barely touches the split.
  }
  const variant = resolveInstallPromptVariant(persisted, Math.random());
  if (variant !== persisted) {
    try {
      window.localStorage.setItem(VARIANT_STORAGE_KEY, variant);
    } catch {
      // See above.
    }
  }
  return variant;
}

export function InstallPromptBanner(): null {
  const pathname = usePathname();
  const suppressed = isSuppressedRoute(pathname);

  useEffect(() => {
    if (suppressed) {
      // Client-side nav can carry an already-shown toast onto a suppressed
      // route — clear it rather than letting it ride along.
      toast.dismiss(TOAST_ID);
      return;
    }
    if (isStandalone()) return;
    if (isSnoozed()) return;

    let shownVariant: ShownVariant | null = null;
    let open = false;
    let exposed = false;

    const context = (variant: ShownVariant) => ({
      experiment_id: INSTALL_PROMPT_EXPERIMENT_ID,
      variant,
    });

    // Every close goes through here. sonner fires `onDismiss` for our own
    // toast.dismiss() calls as well as for a swipe, so the `open` latch is
    // what separates a user dismissal (snooze + track) from a programmatic one.
    const close = (byUser: boolean): void => {
      if (!open) return;
      open = false;
      if (byUser && shownVariant) {
        snooze();
        trackEvent("pwa_install_prompt_dismissed", context(shownVariant));
      }
      toast.dismiss(TOAST_ID);
    };
    const dismissByUser = (): void => close(true);

    const show = (variant: ShownVariant, card: ReactElement): void => {
      shownVariant = variant;
      open = true;
      if (!exposed) {
        exposed = true;
        trackEvent("pwa_install_prompt_shown", context(variant));
      }
      toast.custom(() => card, {
        id: TOAST_ID,
        duration: Infinity,
        onDismiss: dismissByUser,
      });
    };

    // iOS Safari: no `beforeinstallprompt` exists; show the A2HS hint directly.
    if (isIosBrowser()) {
      show("ios_hint", <IosInstallHint onDismiss={dismissByUser} />);
      return () => close(false);
    }

    const variant = assignVariant();
    let deferredPrompt: BeforeInstallPromptEvent | null = null;

    const install = async (): Promise<void> => {
      const prompt = deferredPrompt;
      if (!prompt) return;
      deferredPrompt = null;
      trackEvent("pwa_install_prompt_engaged", context(variant));
      try {
        await prompt.prompt();
        const { outcome } = await prompt.userChoice;
        trackEvent("pwa_install_prompt_resolved", {
          ...context(variant),
          outcome,
        });
        if (outcome === "dismissed") {
          snooze();
        }
      } catch {
        // Chrome will reject if prompt() was already shown this session — fall
        // through; the toast hides regardless so the user is not nagged.
      }
      close(false);
    };

    const onBeforeInstall = (event: Event): void => {
      // Stop Chrome's default behaviour (the deprecated mini-infobar, where
      // it still exists) so we can drive the prompt from our own UI.
      event.preventDefault();
      deferredPrompt = event as BeforeInstallPromptEvent;
      // Chrome re-fires `beforeinstallprompt` on SPA route changes. The mount
      // effect only runs once (root layout never remounts), so without this
      // re-check a dismissed toast would pop back on the very next URL change
      // instead of staying gone for the cooldown window. We still stash the
      // deferred event above so a later post-snooze fire can prompt.
      if (isSnoozed()) return;
      const Card = variant === "slide" ? SlideInstallCard : EmberInstallCard;
      show(variant, <Card onInstall={install} onDismiss={dismissByUser} />);
    };
    const onAppInstalled = (): void => {
      deferredPrompt = null;
      close(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      close(false);
    };
  }, [suppressed]);

  return null;
}

interface InstallCardProps {
  onInstall: () => Promise<void>;
  onDismiss: () => void;
}

const CARD_CLASS = "relative w-[360px] max-w-[calc(100vw-2rem)]";

/**
 * The shared liquid-glass material as a background sibling: no filtered
 * element is an ancestor of the text, which keeps glyphs crisp on Retina.
 */
function GlassLayer() {
  return (
    <span
      aria-hidden
      data-liquid-glass="elevated"
      className="pointer-events-none absolute inset-0 rounded-[inherit] border"
    />
  );
}

/** A 28px disc on the card's corner; `before:` widens the hit area to 44px. */
function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      aria-label="Dismiss"
      onClick={onDismiss}
      data-liquid-glass-control
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute -right-2 -top-2 z-10 grid size-7 place-items-center rounded-full border outline-none transition-colors before:absolute before:-inset-2 focus-visible:ring-[3px]"
    >
      <X className="size-3.5" aria-hidden />
    </button>
  );
}

// The ember card's highlight follows the pointer through CSS variables, so
// moving the mouse never re-renders the toast.
function followPointer(event: PointerEvent<HTMLElement>): void {
  const target = event.currentTarget;
  const rect = target.getBoundingClientRect();
  target.style.setProperty(
    "--pointer-x",
    `${String(event.clientX - rect.left)}px`,
  );
  target.style.setProperty(
    "--pointer-y",
    `${String(event.clientY - rect.top)}px`,
  );
}

function resetPointer(event: PointerEvent<HTMLElement>): void {
  event.currentTarget.style.removeProperty("--pointer-x");
  event.currentTarget.style.removeProperty("--pointer-y");
}

/** Variant `ember`: the whole card is one ember-glass install button. */
function EmberInstallCard({ onInstall, onDismiss }: InstallCardProps) {
  const [pending, setPending] = useState(false);

  return (
    <div className={CARD_CLASS}>
      <button
        type="button"
        disabled={pending}
        aria-busy={pending}
        onClick={() => {
          setPending(true);
          void onInstall();
        }}
        onPointerDown={followPointer}
        onPointerMove={followPointer}
        onPointerLeave={resetPointer}
        className="focus-visible:ring-ring/50 duration-(--duration-slow) ease-(--ease-spring) group relative isolate flex w-full items-center gap-3 rounded-[22px] p-3.5 text-left text-white outline-none transition-transform focus-visible:ring-[3px] active:scale-[0.97] motion-reduce:transition-none"
      >
        <GlassLayer />
        <span
          aria-hidden
          className="install-ember-tint pointer-events-none absolute inset-0 rounded-[inherit]"
        />
        <AtriamMark decorative className="relative size-11 drop-shadow-md" />
        <span className="relative min-w-0 flex-1 [text-shadow:0_1px_2px_oklch(0_0_0/0.25)]">
          <span className="block text-[15px] font-semibold leading-tight">
            Install Atriam
          </span>
          <span className="mt-0.5 block text-xs leading-snug">
            Push alerts and one-tap access
          </span>
        </span>
        <span
          aria-hidden
          className="duration-(--duration-slow) ease-(--ease-spring) relative grid size-9 shrink-0 place-items-center rounded-full border border-white/40 bg-white/15 transition-transform group-hover:translate-y-0.5 motion-reduce:transition-none"
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </span>
      </button>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
}

// Release past this share of the track to install; a tap only nudges.
const SLIDE_COMPLETE_AT = 0.85;
const SLIDE_TAP_SLOP_PX = 4;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

interface SlideDrag {
  originX: number;
  startX: number;
  travel: number;
  moved: boolean;
}

/** Variant `slide`: drag the app icon along a glass track to install. */
function SlideInstallCard({ onInstall, onDismiss }: InstallCardProps) {
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [pending, setPending] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<SlideDrag | null>(null);

  const install = (): void => {
    if (pending) return;
    setProgress(1);
    setPending(true);
    void onInstall();
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    // sonner reads a sideways drag on a toast as swipe-to-dismiss; this one
    // is ours.
    event.stopPropagation();
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (pending || !track || !thumb) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const travel = track.clientWidth - thumb.offsetWidth - 2 * thumb.offsetTop;
    dragRef.current = {
      originX: event.clientX,
      startX: event.clientX - progress * travel,
      travel,
      moved: false,
    };
    setDragging(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const current = dragRef.current;
    if (!current) return;
    if (Math.abs(event.clientX - current.originX) > SLIDE_TAP_SLOP_PX) {
      current.moved = true;
    }
    setProgress(clamp01((event.clientX - current.startX) / current.travel));
  };

  // Installing inside pointerup keeps Chrome's user activation for prompt().
  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    const current = dragRef.current;
    if (!current) return;
    dragRef.current = null;
    setDragging(false);
    if (!current.moved) {
      setNudge(true);
      return;
    }
    const released = clamp01((event.clientX - current.startX) / current.travel);
    if (released >= SLIDE_COMPLETE_AT) {
      install();
    } else {
      setProgress(0);
    }
  };

  const onPointerCancel = (): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    setProgress(0);
  };

  // ARIA slider keys; Enter/Space/End install outright for keyboard users.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (pending) return;
    if (event.key === "Enter" || event.key === " " || event.key === "End") {
      event.preventDefault();
      install();
      return;
    }
    const step =
      event.key === "ArrowRight" || event.key === "ArrowUp"
        ? 0.25
        : event.key === "ArrowLeft" || event.key === "ArrowDown"
          ? -0.25
          : event.key === "Home"
            ? -1
            : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = clamp01(progress + step);
    if (next === 1) {
      install();
    } else {
      setProgress(next);
    }
  };

  return (
    <div className={CARD_CLASS}>
      <div className="relative isolate rounded-3xl p-3">
        <GlassLayer />
        <div className="relative px-1 pb-2.5">
          <p className="text-foreground text-[15px] font-semibold leading-tight">
            Install Atriam
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Push alerts and one-tap access
          </p>
        </div>
        <div
          ref={trackRef}
          style={{ "--progress": progress } as CSSProperties}
          data-dragging={dragging || undefined}
          data-pending={pending || undefined}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          className="install-slide-track h-13 relative cursor-grab touch-none select-none overflow-hidden rounded-full active:cursor-grabbing"
        >
          <span
            aria-hidden
            className="install-slide-fill install-ember-tint pointer-events-none absolute inset-y-0 left-0 rounded-full"
          />
          <span
            aria-hidden
            className="install-slide-hint text-muted-foreground pointer-events-none absolute inset-0 grid place-items-center pl-12 text-sm font-medium"
          >
            {pending ? "Opening install…" : "Slide to install"}
          </span>
          <div
            ref={thumbRef}
            role="slider"
            tabIndex={0}
            aria-label="Slide to install Atriam"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            data-nudge={nudge || undefined}
            onKeyDown={onKeyDown}
            onAnimationEnd={() => setNudge(false)}
            className="install-slide-thumb focus-visible:ring-ring/50 absolute top-1 size-11 rounded-full outline-none focus-visible:ring-[3px]"
          >
            <AtriamMark
              decorative
              className="pointer-events-none size-11 drop-shadow-md"
            />
          </div>
        </div>
      </div>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
}

/** iOS Safari has no install API: point at the Share sheet instead. */
function IosInstallHint({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className={CARD_CLASS}>
      <div className="relative isolate flex items-center gap-3 rounded-[22px] p-3.5">
        <GlassLayer />
        <AtriamMark decorative className="relative size-11" />
        <div className="relative min-w-0 flex-1">
          <p className="text-foreground text-[15px] font-semibold leading-tight">
            Install Atriam
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
            Tap <Share aria-hidden className="inline size-3.5 align-[-2px]" />{" "}
            Share, then Add to Home Screen
          </p>
        </div>
      </div>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
}
